import React, { useState } from 'react';
import './ImportHelp.css';
import Icon from './Icon';

// Contextual "how should my file look?" helper shown beside each import button.
// It ONLY documents the format the importer already expects (see Contacts.js
// parseCSV + Properties.js parseProperties) and offers a blank template to fill
// in — so a user with a differently-shaped export (another MLS, a CRM dump) can
// reformat their data to match. It never changes how importing works.

// Address columns are assembled into one address, so we group them for clarity.
const PROPERTY_COLUMNS = [
  { field: 'MLS / Listing #', headers: ['Listing Number'], need: 'recommended', hint: 'Used to de-duplicate re-imports.' },
  { field: 'Address', headers: ['Street Number', 'Street Direction', 'Street Name', 'Street Suffix', 'Unit'], need: 'required', hint: 'Also accepts Street Number Modifier, Street Post Direction. These columns are joined into one address.' },
  { field: 'City / State / ZIP', headers: ['City', 'State', 'Zip Code'], need: 'recommended', hint: '' },
  { field: 'Sale price', headers: ['Selling Price'], need: 'optional', hint: '' },
  { field: 'Sold date', headers: ['Selling Date'], need: 'recommended', hint: 'Format M/D/YYYY (e.g. 3/15/2024). A row with a valid sold date is marked Sold.' },
  { field: 'Owners', headers: ['Owner Name', 'Owner Name 2'], need: 'optional', hint: '' },
  { field: 'Details', headers: ['Bedrooms', 'Bathrooms', 'Square Footage', 'Status'], need: 'optional', hint: '' },
];

const PROPERTY_TEMPLATE = {
  header: 'Listing Number,Street Number,Street Direction,Street Name,Street Suffix,Unit,City,State,Zip Code,Selling Price,Selling Date,Owner Name,Owner Name 2,Bedrooms,Bathrooms,Square Footage,Status',
  example: '2245678,1420,NE,Maple,St,,Bellevue,WA,98004,1250000,3/15/2024,Jane Doe,John Doe,4,2.5,2650,Sold',
};

const HELP = {
  contacts: {
    title: 'Import Contacts',
    // Step-by-step for the most common source (NWMLS Transaction Desk). Shown at the
    // top of the help so agents can just follow it; the format details below are for
    // other sources / advanced users.
    sourceGuide: {
      title: 'Importing your NWMLS contacts',
      intro: 'Bring your entire NWMLS contact list into iBetterAgent in two quick steps — export from NWMLS, then import the file.',
      sections: [
        {
          title: 'Step 1 — Export your contacts from NWMLS',
          steps: [
            'Log in to NWMLS.',
            'Open Transaction Desk.',
            'Go to Contacts.',
            'Scroll all the way to the bottom of your contact list so every contact is loaded.',
            'In the upper-right corner, click the Select All checkbox to select all of your contacts.',
            'Click the basket (actions) icon, then in the upper-right toolbar click the Export icon (left icon).',
            "Save the file to your computer (it will be a .csv file — note where it's saved).",
          ],
        },
        {
          title: 'Step 2 — Import the file into iBetterAgent',
          steps: [
            'Go to iBetterAgent.com and log in.',
            'Open your Contacts screen.',
            'Click Import Contacts.',
            'Choose the .csv file you just saved from NWMLS.',
            'Done — all your contacts are imported into iBetterAgent.',
          ],
        },
      ],
    },
    intro: 'The file is a .csv with a header row. Column names are matched case-insensitively and can be in any order; extra columns are ignored. Each row needs at least a name or an email.',
    columns: [
      { field: 'First name', headers: ['name_first', 'first name', 'firstname', 'first'], need: 'recommended', hint: '' },
      { field: 'Last name', headers: ['name_last', 'last name', 'lastname', 'last'], need: 'recommended', hint: '' },
      { field: 'Email', headers: ['email1', 'email', 'email address', 'e-mail'], need: 'recommended', hint: 'If several are listed, only the first is used.' },
      { field: 'Middle name', headers: ['name_middle', 'middle name', 'middlename', 'middle'], need: 'optional', hint: '' },
      { field: 'Mobile phone', headers: ['telephone_mobile', 'mobile', 'cell', 'cell phone'], need: 'optional', hint: '' },
      { field: 'Home phone', headers: ['telephone_home', 'home', 'home phone'], need: 'optional', hint: '' },
    ],
    template: {
      header: 'name_first,name_last,email1,telephone_mobile,telephone_home',
      example: 'Jane,Doe,jane.doe@example.com,206-555-0101,206-555-0102',
    },
    filename: 'contacts-import-template.csv',
    notes: [
      'A row imports if it has a first name, last name, OR email.',
      'Imported contacts start as Leads — set status and heat afterward.',
      'WeChat, Instagram, birthday, and language are not imported — add them in-app. (Birthday reminders need the birthday entered manually.)',
    ],
  },
  listing: {
    title: "Import Listing Agent Sales",
    sourceGuide: {
      title: 'Producing your listing-agent sales file (NWMLS)',
      intro: 'Export your closed listing-side sales from NWMLS, then import the file here.',
      sections: [
        {
          title: 'Step 1 — Export from NWMLS',
          steps: [
            'In NWMLS, start a property search — Search → property type: General.',
            'Set the status to "Sold".',
            'Enter your broker ID in the "List & Co-List Broker ID" field.',
            'Run the search, then Select All of the results.',
            'Export the results using the "Sold and Status" export.',
            'Save the file as PropertiesListingAgent.csv.',
          ],
        },
        {
          title: 'Step 2 — Import into iBetterAgent',
          steps: [
            'Back here, click Import Listing Agent Sales.',
            'Choose the PropertiesListingAgent.csv file you saved.',
            'Done — your listing-side sales are imported.',
          ],
        },
      ],
    },
    intro: 'The file is a .csv export of the homes you sold on the listing (seller) side — a standard MLS "Sold" export works. Column names are matched case-insensitively, in any order; extra columns are ignored.',
    columns: PROPERTY_COLUMNS,
    template: PROPERTY_TEMPLATE,
    filename: 'listing-agent-sales-template.csv',
    notes: [
      'A row imports if it has an address or city.',
      'Owner Name / Owner Name 2 are automatically matched to your existing Contacts and linked (first/last, 1-character typo tolerance).',
      'Re-importing the same file will not create duplicates (matched by Listing Number, else address + sold date).',
    ],
  },
  buyer: {
    title: "Import Buyer's Agent Sales",
    sourceGuide: {
      title: "Producing your buyer's-agent sales file (NWMLS)",
      intro: 'Export your closed buyer-side sales from NWMLS, then import the file here.',
      sections: [
        {
          title: 'Step 1 — Export from NWMLS',
          steps: [
            'In NWMLS, start a property search — Search → property type: General.',
            'Set the status to "Sold".',
            'Enter your broker ID in the "Buyer & Co-Buyer Broker ID" field.',
            'Run the search, then Select All of the results.',
            'Export the results using the "Sold and Status" export.',
            'Save the file as PropertiesBuyersAgent.csv.',
          ],
        },
        {
          title: 'Step 2 — Import into iBetterAgent',
          steps: [
            "Back here, click Import Buyer's Agent Sales.",
            'Choose the PropertiesBuyersAgent.csv file you saved.',
            'Done — your buyer-side sales are imported.',
          ],
        },
      ],
    },
    intro: 'The file is a .csv export of the homes you closed on the buyer side — the same layout as the listing export. Column names are matched case-insensitively, in any order; extra columns are ignored.',
    columns: PROPERTY_COLUMNS,
    template: PROPERTY_TEMPLATE,
    filename: 'buyer-agent-sales-template.csv',
    notes: [
      'A row imports if it has an address or city.',
      'Owner columns are not auto-linked on the buyer side (in a sold export the owner is the seller, not your buyer) — link the buyer contact manually if you want it tied to the sale.',
      'Re-importing the same file will not create duplicates (matched by Listing Number, else address + sold date).',
    ],
  },
};

const NEED_LABEL = { required: 'Required', recommended: 'Recommended', optional: 'Optional' };

function download(filename, text) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// `type` is one of: 'contacts' | 'listing' | 'buyer'.
function ImportHelp({ type }) {
  const [open, setOpen] = useState(false);
  const h = HELP[type];
  if (!h) return null;

  return (
    <>
      <button type="button" className="import-help-btn" title="What should my file look like?"
        aria-label="Import file format help" onClick={() => setOpen(true)}>
        <Icon name="info" size={16} />
      </button>

      {open && (
        <div className="import-help-overlay" onClick={() => setOpen(false)} role="dialog" aria-modal="true">
          <div className="import-help-modal" onClick={e => e.stopPropagation()}>
            <div className="import-help-head">
              <h3>{h.title}</h3>
              <button type="button" className="import-help-close" aria-label="Close" onClick={() => setOpen(false)}>
                <Icon name="close" size={18} />
              </button>
            </div>

            {h.sourceGuide && (
              <div className="ih-source">
                <div className="ih-source-title"><Icon name="import" size={16} /> {h.sourceGuide.title}</div>
                {h.sourceGuide.intro && <p className="ih-source-intro">{h.sourceGuide.intro}</p>}
                {h.sourceGuide.sections.map((sec, si) => (
                  <div key={si} className="ih-source-section">
                    <div className="ih-source-step-title">{sec.title}</div>
                    <ol className="ih-source-steps">
                      {sec.steps.map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                  </div>
                ))}
              </div>
            )}

            <div className="ih-section-label">{h.sourceGuide ? 'File format (for other sources)' : 'File format'}</div>
            <p className="import-help-intro">{h.intro}</p>

            <table className="import-help-table">
              <thead>
                <tr><th>Field</th><th>Column name(s) — any one works</th><th></th></tr>
              </thead>
              <tbody>
                {h.columns.map(c => (
                  <tr key={c.field}>
                    <td className="ih-field">{c.field}</td>
                    <td>
                      {c.headers.map(x => <code key={x} className="ih-code">{x}</code>)}
                      {c.hint && <div className="ih-hint">{c.hint}</div>}
                    </td>
                    <td><span className={`ih-need ih-need-${c.need}`}>{NEED_LABEL[c.need]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="import-help-example">
              <div className="ih-example-label">Example (header row + one record):</div>
              <pre className="ih-example-pre">{h.template.header}{'\n'}{h.template.example}</pre>
            </div>

            {h.notes && h.notes.length > 0 && (
              <ul className="import-help-notes">
                {h.notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            )}

            <div className="import-help-actions">
              <button type="button" className="import-help-download"
                onClick={() => download(h.filename, `${h.template.header}\n`)}>
                <Icon name="file-down" size={16} /> Download blank template (.csv)
              </button>
              <button type="button" className="import-help-done" onClick={() => setOpen(false)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ImportHelp;
